import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as d3 from 'd3';

const pastelPalette = [
    "#A7C7E7",
    "#BDE0FE",
    "#CDB4DB",
    "#F9C6C9",
    "#FFE5B4",
    "#F7D9C4",
    "#B7E4C7",
    "#C3E5AE",
    "#D4F1F4",
    "#FDE2E4"
];

const projectPalette = {
    stroke: "#059669",
    strokeHover: "#047857",
    assignedBase: "#34d399"
};

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
const approxTextWidth = (text, fontSize) => (text?.length || 0) * fontSize * 0.58;
const areSetsEqual = (left, right) => {
    if (left === right) return true;
    const a = left instanceof Set ? left : new Set(left || []);
    const b = right instanceof Set ? right : new Set(right || []);
    if (a.size !== b.size) return false;
    for (const value of a) {
        if (!b.has(value)) return false;
    }
    return true;
};

const hashString = (value) => {
    const str = String(value || '');
    let hash = 0;
    for (let i = 0; i < str.length; i += 1) {
        hash = (hash << 5) - hash + str.charCodeAt(i);
        hash |= 0;
    }
    return Math.abs(hash);
};

const adjustColor = (colorValue, { h = 0, s = 0, l = 0 } = {}) => {
    const color = d3.hsl(colorValue);
    if (Number.isNaN(color.h)) return colorValue;
    color.h = (color.h + h + 360) % 360;
    color.s = clamp(color.s + s, 0, 1);
    color.l = clamp(color.l + l, 0, 1);
    return color.formatHex();
};

const isRoleNode = (node) => {
    if (!node?.data) return false;
    if (node.data.nodeType === "project") return false;
    const hasChildren = Boolean(node.children && node.children.length);
    return node.data.parent_id && !hasChildren;
};

const getNodeKind = (node) => node?.data?.nodeType || (isRoleNode(node) ? "role" : "circle");

const colorCache = new WeakMap();

const getNodeKey = (node) => {
    if (!node) return '';
    if (node?.data?.id !== undefined && node?.data?.id !== null) return `id:${node.data.id}`;
    if (node?.data?.name) return `name:${node.data.name}`;
    return `depth:${node.depth ?? 0}:${node.index ?? 0}`;
};

const deriveColorFromParent = (parentColor, nodeKey) => {
    const seed = hashString(nodeKey);
    const hueShift = (seed % 11) - 5;
    const lightShift = ((seed >> 3) % 8) - 4;
    const color = d3.hsl(parentColor);
    if (Number.isNaN(color.h)) return parentColor;
    color.h = (color.h + hueShift + 360) % 360;
    color.s = clamp(color.s + 0.02, 0.25, 0.55);
    color.l = clamp(color.l + lightShift / 100, 0.78, 0.92);
    return color.formatHex();
};

const getNodeBaseColor = (node) => {
    if (!node) return pastelPalette[0];
    if (colorCache.has(node)) return colorCache.get(node);
    const nodeKey = getNodeKey(node);
    let base = node?.data?.color;
    if (base && !d3.color(base)) {
        base = null;
    }
    if (!base) {
        if (node?.parent) {
            base = deriveColorFromParent(getNodeBaseColor(node.parent), nodeKey);
        } else {
            base = pastelPalette[hashString(nodeKey) % pastelPalette.length];
        }
    }
    colorCache.set(node, base);
    return base;
};

const getNodePalette = (node) => {
    const kind = getNodeKind(node);
    if (kind === "project") return projectPalette;
    const base = getNodeBaseColor(node);
    const isRoleLike = kind === "role";
    const toned = adjustColor(base, {
        s: isRoleLike ? 0.08 : 0.02,
        l: isRoleLike ? 0.04 : 0.02
    });
    return {
        assignedBase: toned,
        stroke: adjustColor(toned, { l: -0.18, s: 0.08 }),
        strokeHover: adjustColor(toned, { l: -0.26, s: 0.1 })
    };
};

const CirclePack = ({ data, onCircleClick, openMeetingCircleIds, valueAccessor, tight = false, selectedCircleId }) => {
    const svgRef = useRef(null);
    const [dimensions, setDimensions] = useState({ width: 800, height: 600 });
    const meetingIdsRef = useRef(new Set());
    const meetingStroke = "#10b981";
    const focusIdRef = useRef(null);
    const zoomRef = useRef(null);
    const nodesByIdRef = useRef(new Map());
    const selectedIdRef = useRef(null);
    const selectedCircleIdRef = useRef(null);
    selectedCircleIdRef.current = selectedCircleId;
    const lowPowerDevice = useMemo(() => {
        if (typeof navigator === "undefined") return false;
        const cores = Number(navigator.hardwareConcurrency || 0);
        if (!Number.isFinite(cores) || cores <= 0) return false;
        return cores <= 4;
    }, []);

    useEffect(() => {
        const parent = svgRef.current?.parentElement;
        if (!parent) return undefined;
        let frame;
        const updateDimensions = () => {
            const rect = parent.getBoundingClientRect();
            const nextWidth = Math.max(0, Math.floor(rect.width));
            const nextHeight = Math.max(0, Math.floor(rect.height)) || 600;
            setDimensions((prev) => {
                if (prev.width === nextWidth && prev.height === nextHeight) return prev;
                return { width: nextWidth, height: nextHeight };
            });
        };
        updateDimensions();

        if (typeof ResizeObserver !== "undefined") {
            const observer = new ResizeObserver(() => {
                if (frame) cancelAnimationFrame(frame);
                frame = requestAnimationFrame(updateDimensions);
            });
            observer.observe(parent);
            return () => {
                observer.disconnect();
                if (frame) cancelAnimationFrame(frame);
            };
        }

        window.addEventListener("resize", updateDimensions);
        return () => {
            window.removeEventListener("resize", updateDimensions);
            if (frame) cancelAnimationFrame(frame);
        };
    }, []);

    useEffect(() => {
        if (!data || !svgRef.current) return;

        const { width, height } = dimensions;
        if (!width || !height || width < 10 || height < 10) return; // skip if container collapsed

        const svg = d3.select(svgRef.current);
        svg.selectAll("*").remove();

        try {
        const marginScale = tight ? 0.02 : 0.04;
        const margin = Math.max(Math.min(width, height) * marginScale, 4); // Dynamic margin with minimum
        const displayWidth = Math.max(width - margin * 2, 10);
        const displayHeight = Math.max(height - margin * 2, 10);

        // --- DATA PROCESSING ---
        const safeValueAccessor = valueAccessor || (() => 1);
        const shouldScaleRoles = !valueAccessor;
        const roleDepthScale = (node) => {
            if (!shouldScaleRoles) return 1;
            const isRoleLike = isRoleNode(node) || node?.data?.nodeType === "project";
            if (!isRoleLike) return 1;
            if (node.parent && node.parent.depth === 0) return 2;
            const depth = Number(node.depth || 0);
            const scale = 1.05 - depth * 0.05;
            return Math.max(0.9, scale);
        };

        const root = d3.hierarchy(data);
        root.eachAfter((node) => {
            if (!node.children || node.children.length === 0) {
                const raw = safeValueAccessor(node.data);
                const value = Number(raw);
                const base = Number.isNaN(value) ? 0 : Math.max(0, value);
                node.value = base * roleDepthScale(node);
                return;
            }
            node.value = d3.sum(node.children, (child) => child.value || 0);
        });
        root.sort((a, b) => b.value - a.value);
        const nodeCount = root.descendants().length;
        const performanceMode = lowPowerDevice || nodeCount > 120;

        // --- FILTERS & DEFINITIONS ---
        if (!performanceMode) {
            const defs = svg.append("defs");
            const shadowFilter = defs.append("filter")
                .attr("id", "premium-shadow")
                .attr("height", "200%");

            shadowFilter.append("feGaussianBlur")
                .attr("in", "SourceAlpha")
                .attr("stdDeviation", 8)
                .attr("result", "blur");

            shadowFilter.append("feOffset")
                .attr("in", "blur")
                .attr("dx", 0)
                .attr("dy", 8)
                .attr("result", "offsetBlur");

            shadowFilter.append("feFlood")
                .attr("flood-color", "rgba(0,0,0,0.15)")
                .attr("result", "color");

            shadowFilter.append("feComposite")
                .attr("in", "color")
                .attr("in2", "offsetBlur")
                .attr("operator", "in")
                .attr("result", "shadow");

            const feMerge = shadowFilter.append("feMerge");
            feMerge.append("feMergeNode").attr("in", "shadow");
            feMerge.append("feMergeNode").attr("in", "SourceGraphic");
        }
        const nodesById = new Map();
        root.descendants().forEach((node) => {
            if (node?.data?.id !== undefined && node?.data?.id !== null) {
                nodesById.set(String(node.data.id), node);
            }
        });
        nodesByIdRef.current = nodesById;

        const getPadding = (d) => {
            // Slightly higher padding near the root keeps siblings away from the shell
            if (d.depth === 0) return tight ? 12 : 22;
            // Compact padding once we are deeper to preserve room for labels
            return d.children && d.children.length > 0 ? (tight ? 8 : 14) : (tight ? 5 : 8);
        };

        const pack = d3.pack()
            .size([displayWidth, displayHeight])
            .padding(getPadding);

        pack(root);
        // --- POST-PACK LAYOUT ADJUSTMENT ---
        const shiftChildren = (node) => {
            if (!node.children || node.children.length === 0) return;

            let maxChildBottom = -Infinity;
            node.children.forEach(child => {
                maxChildBottom = Math.max(maxChildBottom, child.y + child.r);
            });

            const parentBottom = node.y + node.r;
            const targetBottom = parentBottom - margin;
            const shiftAmount = targetBottom - maxChildBottom;
            const cappedShift = Math.min(shiftAmount, node.r * 0.08);

            if (cappedShift > 0) {
                const applyShift = (n, dy) => {
                    n.y += dy;
                    if (n.children) {
                        n.children.forEach(child => applyShift(child, dy));
                    }
                };

                node.children.forEach(child => applyShift(child, cappedShift));
            }

            node.children.forEach(shiftChildren);
        };

        shiftChildren(root);

        const keepChildrenInsideParent = (node) => {
            if (!node.children || node.children.length === 0) return;

            node.children.forEach(keepChildrenInsideParent);

            let maxExtent = 0;
            node.children.forEach(child => {
                const distanceToCenter = Math.hypot(child.x - node.x, child.y - node.y);
                maxExtent = Math.max(maxExtent, distanceToCenter + child.r);
            });

            const safetyMargin = Math.max(3, node.r * 0.012);
            const allowedRadius = Math.max(node.r - safetyMargin, 0);

            if (maxExtent > allowedRadius && maxExtent > 0) {
                const scale = allowedRadius / maxExtent;
                const scaleBranch = (n) => {
                    n.x = node.x + (n.x - node.x) * scale;
                    n.y = node.y + (n.y - node.y) * scale;
                    n.r *= scale;
                    if (n.children) {
                        n.children.forEach(scaleBranch);
                    }
                };
                node.children.forEach(scaleBranch);
            }
        };

        keepChildrenInsideParent(root);

        // --- RENDERING ---
        const g = svg.append("g")
            .attr("transform", `translate(${width / 2},${height / 2})`);

        let view;
        let focus = root;
        if (selectedCircleIdRef.current !== undefined && selectedCircleIdRef.current !== null) {
            focusIdRef.current = String(selectedCircleIdRef.current);
        }
        if (!focusIdRef.current && root?.data?.id !== undefined) {
            focusIdRef.current = String(root.data.id);
        }

        const hasMeeting = (node) => node?.data?.id !== undefined && meetingIdsRef.current.has(String(node.data.id));
        const isProjectNode = (node) => node?.data?.nodeType === "project";
        const hasAssignee = (node) => {
            if (isProjectNode(node)) return true;
            const ownerId = node?.data?.owner_id;
            if (ownerId !== null && ownerId !== undefined && ownerId !== '') return true;
            const ownerIds = node?.data?.owner_ids;
            return Array.isArray(ownerIds) && ownerIds.some((id) => id !== null && id !== undefined && id !== '');
        };

        // Color Palette - Circles vs Roles
        const colors = {
            meetingStroke: meetingStroke, // Emerald 500
            text: "#1e293b", // Slate 800
            textLight: "#475569" // Slate 600
        };

        const baseAlpha = 0.1;
        const alphaStep = 0.07;
        const maxAlpha = 0.65;
        const getAssignedFill = (d, boost = 0) => {
            const alpha = Math.min(maxAlpha, baseAlpha + d.depth * alphaStep + boost);
            const color = d3.color(getNodePalette(d).assignedBase);
            if (color) {
                color.opacity = alpha;
                return color.formatRgb();
            }
            return getNodePalette(d).assignedBase;
        };

        const getUnassignedFill = (d) => {
            if (getNodeKind(d) === "project") return "#ecfdf3";
            const base = getNodePalette(d).assignedBase;
            const color = d3.color(base);
            if (color) {
                color.opacity = isRoleNode(d) ? 0.1 : 0.07;
                return color.formatRgb();
            }
            return base;
        };

        const getHoverFill = (d) => {
            if (hasAssignee(d)) return getAssignedFill(d, 0.1);
            const base = getNodePalette(d).assignedBase;
            return adjustColor(base, { l: 0.06, s: 0.02 });
        };

        const getBaseFill = (d) => {
            if (!hasAssignee(d)) return getUnassignedFill(d);
            return getAssignedFill(d);
        };

        // Helper to update styles based on state
        const updateCircleStyles = () => {
            const styled = performanceMode
                ? circle
                : circle.transition().duration(300);
            styled
                .style("stroke", d => hasMeeting(d) ? colors.meetingStroke : getNodePalette(d).stroke)
                .style("stroke-width", d => d.depth === 0 ? 4 : (d.children ? 2.5 : 1.5))
                .style("fill", d => {
                    if (d === focus) return hasAssignee(d) ? getAssignedFill(d, 0.08) : getUnassignedFill(d);
                    return getBaseFill(d);
                });
        };

        // Circles
        const circle = g.selectAll("circle")
            .data(root.descendants())
            .join("circle")
            .attr("r", d => d.r)
            .style("fill", d => getBaseFill(d))
            .style("stroke", d => hasMeeting(d) ? colors.meetingStroke : getNodePalette(d).stroke)
            .style("stroke-width", d => d.depth === 0 ? 4 : (d.children ? 2.5 : 1.5))
            .style("filter", d => (!performanceMode && d.depth > 0 ? "url(#premium-shadow)" : "none"))
            .style("cursor", "pointer")
            .style("transition", performanceMode ? "none" : "stroke 0.3s ease, stroke-width 0.3s ease, fill 0.3s ease")
            .on("click", (event, d) => {
                if (focus !== d) {
                    zoom(event, d);
                }
                event.stopPropagation();
                if (onCircleClick) {
                    onCircleClick(d.data);
                }
            });
        if (!performanceMode) {
            circle
                .on("mouseover", function (event, d) {
                    if (d !== focus) {
                        d3.select(this)
                            .style("stroke", hasMeeting(d) ? colors.meetingStroke : getNodePalette(d).strokeHover)
                            .style("stroke-width", d.depth === 0 ? 6 : (d.children ? 4 : 3))
                            .style("fill", getHoverFill(d));
                    }
                })
                .on("mouseout", function (event, d) {
                    if (d !== focus) {
                        d3.select(this)
                            .style("stroke", hasMeeting(d) ? colors.meetingStroke : getNodePalette(d).stroke)
                            .style("stroke-width", d.depth === 0 ? 4 : (d.children ? 2.5 : 1.5))
                            .style("fill", getBaseFill(d));
                    }
                });
        }

        // Labels
        const labelGroup = g.append("g").style("pointer-events", "none");

        const label = labelGroup.selectAll("text")
            .data(root.descendants())
            .join("text")
            // Always visible logic
            .style("fill-opacity", 1)
            .style("display", "inline")
            .attr("text-anchor", "middle")
            .style("font-family", "'Inter', system-ui, -apple-system, sans-serif")
            .style("font-weight", d => d.children ? "700" : "600")
            .style("fill", colors.text)
            .style("text-shadow", performanceMode ? "none" : "0 2px 4px rgba(255,255,255,0.9)");

        // Libellés calculés une fois, en unités du pack (zoom 1), puis seulement mis à l'échelle au zoom : le résultat
        // ne dépend plus du zoom au moment du dessin (dessiner zoomé sur un rôle puis dézoomer faisait perdre des noms).
        const getLabelFontSize = (node) => {
            if (node.children && node.children.length > 0) {
                return Math.min(26, Math.max(14, node.r / 4.6));
            }
            return Math.min(20, Math.max(10, node.r / 2.9));
        };

        // Marge intérieure proportionnelle au rayon (plafonnée) : même un petit rôle garde un libellé, lisible en zoomant.
        const getLabelPadding = (radius, isParent) => {
            const safeRadius = Math.max(0, Number(radius || 0));
            return isParent ? Math.min(18, safeRadius * 0.12) : Math.min(12, safeRadius * 0.18);
        };

        // Largeurs mesurées une fois à une taille de référence : la largeur d'un texte est proportionnelle à sa police.
        const REFERENCE_FONT = 100;
        const widthCache = new Map();
        const labelMeasure = labelGroup.append("text")
            .style("font-family", "'Inter', system-ui, -apple-system, sans-serif")
            .style("font-size", `${REFERENCE_FONT}px`)
            .style("visibility", "hidden");
        const referenceWidth = (content, weight) => {
            const key = `${weight}|${content}`;
            if (!widthCache.has(key)) {
                labelMeasure.style("font-weight", weight).text(content);
                const measured = labelMeasure.node()?.getComputedTextLength() || 0;
                // Sans rendu (onglet caché, tests), on estime plutôt que de tout croire de largeur nulle.
                widthCache.set(key, measured > 0 ? measured : approxTextWidth(content, REFERENCE_FONT));
            }
            return widthCache.get(key);
        };
        const widthAt = (content, weight, fontSize) => (referenceWidth(content, weight) * fontSize) / REFERENCE_FONT;

        const splitWord = (word, weight, fontSize, maxWidth) => {
            if (!word) return [];
            if (widthAt(word, weight, fontSize) <= maxWidth) return [word];
            const parts = [];
            let chunk = '';
            for (const char of word) {
                const candidate = `${chunk}${char}`;
                if (chunk && widthAt(candidate, weight, fontSize) > maxWidth) {
                    parts.push(chunk);
                    chunk = char;
                } else {
                    chunk = candidate;
                }
            }
            if (chunk) parts.push(chunk);
            return parts.length ? parts : [word];
        };

        const widest = (texts, weight, fontSize) =>
            texts.reduce((max, text) => Math.max(max, widthAt(text, weight, fontSize)), 0);

        const wrapLines = (words, weight, fontSize, maxWidth) => {
            const lines = [];
            let current = '';
            words.forEach((word) => {
                splitWord(word, weight, fontSize, maxWidth).forEach((segment) => {
                    const candidate = current ? `${current} ${segment}` : segment;
                    if (!current || widthAt(candidate, weight, fontSize) <= maxWidth) {
                        current = candidate;
                    } else {
                        lines.push(current);
                        current = segment;
                    }
                });
            });
            if (current) lines.push(current);
            return lines;
        };

        // Mise en page d'un libellé en unités du pack : police, lignes, position de la première ligne.
        const layoutLabel = (d) => {
            const name = String(d?.data?.name || '').trim();
            const words = name.split(/\s+/).filter(Boolean);
            if (!words.length) return null;
            const isParent = Boolean(d.children && d.children.length);
            const weight = isParent ? "700" : "600";
            const radius = d.r;
            const padding = getLabelPadding(radius, isParent);
            const maxWidth = radius * 2 - padding * 2;
            const maxHeight = radius * 2 - padding * 2;
            if (!(maxWidth > 0) || !(maxHeight > 0)) return null;
            const lineHeight = 1.1;
            const minFontSize = (isParent ? 10 : 7) * 0.5;
            let fontSize = Math.max(minFontSize, getLabelFontSize(d));
            let lines = [];
            for (let guard = 0; guard < 60; guard += 1) {
                const maxWordWidth = widest(words, weight, fontSize);
                // Le mot le plus long dicte d'emblée la taille, au lieu de descendre pas à pas.
                if (maxWordWidth > maxWidth && fontSize > minFontSize) {
                    fontSize = Math.max(minFontSize, Math.min(fontSize * 0.95, (fontSize * maxWidth) / maxWordWidth));
                    continue;
                }
                lines = wrapLines(words, weight, fontSize, maxWidth);
                const maxLines = Math.max(1, Math.floor(maxHeight / (fontSize * lineHeight)));
                const maxLineWidth = widest(lines, weight, fontSize);
                if ((lines.length <= maxLines && maxLineWidth <= maxWidth) || fontSize <= minFontSize) break;
                fontSize = Math.max(minFontSize, fontSize * 0.92);
            }
            if (!lines.length) lines = wrapLines(words, weight, fontSize, maxWidth);
            const maxLines = Math.max(1, Math.floor(maxHeight / (fontSize * lineHeight)));
            if (lines.length > maxLines) {
                lines = lines.slice(0, maxLines);
                const lastIndex = lines.length - 1;
                const trimmed = lines[lastIndex].trim();
                lines[lastIndex] = trimmed.length > 2 ? `${trimmed.slice(0, -1)}...` : trimmed;
            }
            if (!lines.length) return null;
            // Position de la première ligne : en haut du cercle (cercle parent) ou bloc centré (rôle).
            return { lines, fontSize, lineHeight, parent: isParent, top: -radius + padding };
        };

        // Version légère (mode performance) : une ligne coupée au nombre de caractères, en unités du pack.
        const layoutSimpleLabel = (d) => {
            const name = String(d?.data?.name || '').trim();
            if (!name) return null;
            const radius = d.r;
            const fontSize = Math.max(1, Math.min(d.children ? 18 : 14, radius / (d.children ? 4.8 : 3.4)));
            const maxWidth = radius * 1.6;
            const maxChars = Math.max(1, Math.floor(maxWidth / Math.max(approxTextWidth('M', fontSize), 0.01)));
            const text = name.length > maxChars ? `${name.slice(0, Math.max(1, maxChars - 3))}...` : name;
            return { simple: true, text, fontSize };
        };

        const applyLabelLayout = () => {
            label.each(function (d) {
                const textEl = d3.select(this);
                const layout = performanceMode ? layoutSimpleLabel(d) : layoutLabel(d);
                d.__labelLayout = layout;
                if (!layout) {
                    textEl.text('');
                    return;
                }
                if (layout.simple) {
                    textEl.text(layout.text).attr("dominant-baseline", "middle");
                    return;
                }
                textEl.text(null).attr("dominant-baseline", "alphabetic");
                layout.lines.forEach((line, index) => {
                    textEl.append("tspan")
                        .attr("x", 0)
                        .attr("dy", `${index * layout.lineHeight}em`)
                        .text(line);
                });
            });
            labelMeasure.remove();
        };

        // Taille réelle à l'écran : police et position suivent le zoom, plafonnées en proportion du cercle à l'écran
        // (zoomé sur un rôle, son nom reste à l'échelle du cercle sans occuper tout l'écran) ; trop petit, on cache.
        const MAX_SCREEN_FONT = { parent: 56, leaf: 40, simple: 32 };
        const MAX_SCREEN_FONT_ZOOMED = { parent: 72, leaf: 96 };
        const screenCap = (layout, screenRadius) => {
            if (layout.simple) return MAX_SCREEN_FONT.simple;
            if (layout.parent) return Math.min(MAX_SCREEN_FONT_ZOOMED.parent, Math.max(MAX_SCREEN_FONT.parent, screenRadius * 0.12));
            return Math.min(MAX_SCREEN_FONT_ZOOMED.leaf, Math.max(MAX_SCREEN_FONT.leaf, screenRadius * 0.2));
        };
        const applyLabelScale = (scale) => {
            label.each(function (d) {
                const layout = d.__labelLayout;
                if (!layout) return;
                const textEl = d3.select(this);
                const cap = screenCap(layout, d.r * scale);
                const fontSize = Math.min(layout.fontSize * scale, cap);
                const hidden = performanceMode ? d.r * scale < 14 : fontSize < 3;
                textEl
                    .style("display", hidden ? "none" : "inline")
                    .style("font-size", `${fontSize}px`);
                if (layout.simple) return;
                const y = layout.parent
                    ? layout.top * scale + fontSize
                    : -((layout.lines.length - 1) * layout.lineHeight * fontSize) / 2;
                textEl.selectAll("tspan").attr("y", y);
            });
        };

        // Zoom Logic
        const zoomTo = (v) => {
            const k = Math.min(displayWidth, displayHeight) / v[2];
            view = v;
            label.attr("transform", d => `translate(${(d.x - v[0]) * k},${(d.y - v[1]) * k})`);
            applyLabelScale(k);

            circle.attr("transform", d => `translate(${(d.x - v[0]) * k},${(d.y - v[1]) * k})`);
            circle.attr("r", d => d.r * k);
        };

        const zoom = (event, d) => {
            focus = d;
            focusIdRef.current = d?.data?.id !== undefined ? String(d.data.id) : null;
            selectedIdRef.current = focusIdRef.current;
            updateCircleStyles(); // Update styles to highlight the new focus

            const targetSize = focus.r * 1.85;
            const startView = view || [focus.x, focus.y, targetSize];
            if (performanceMode) {
                zoomTo([focus.x, focus.y, targetSize]);
                return;
            }
            svg.transition()
                .duration(700)
                .ease(d3.easeCubicOut)
                .tween("zoom", () => {
                    const i = d3.interpolateZoom(startView, [focus.x, focus.y, targetSize]);
                    return t => zoomTo(i(t));
                });
        };
        zoomRef.current = (id) => {
            const target = nodesByIdRef.current.get(String(id));
            if (target) {
                zoom(null, target);
            }
        };

        // Initial Zoom
        const initialFocus = root.descendants().find(
            (node) => focusIdRef.current && String(node.data?.id) === String(focusIdRef.current)
        ) || root;
        focus = initialFocus;
        updateCircleStyles(); // Initial style application
        applyLabelLayout();
        const initialSize = tight ? focus.r * 1.85 : (focus.r * 2 + margin);
        zoomTo([focus.x, focus.y, initialSize]);

        // Background Click
        svg.on("click", (event) => {
            zoom(event, root);
            focusIdRef.current = root?.data?.id !== undefined ? String(root.data.id) : null;
            if (onCircleClick) onCircleClick(root.data);
        });

        } catch (err) {
            console.error("CirclePack render error", err);
        }

    }, [data, dimensions, onCircleClick, valueAccessor, tight, lowPowerDevice]);

    useEffect(() => {
        if (selectedCircleId === undefined || selectedCircleId === null) return;
        const id = String(selectedCircleId);
        if (selectedIdRef.current === id) return;
        if (!zoomRef.current) return;
        selectedIdRef.current = id;
        zoomRef.current(id);
    }, [selectedCircleId]);

    useEffect(() => {
        const nextSet = openMeetingCircleIds instanceof Set
            ? openMeetingCircleIds
            : new Set(openMeetingCircleIds || []);
        meetingIdsRef.current = nextSet;
        if (!svgRef.current) return;
        const svg = d3.select(svgRef.current);
        svg.selectAll("circle")
            .style("stroke", d => (
                meetingIdsRef.current.has(String(d.data.id))
                    ? meetingStroke
                    : getNodePalette(d).stroke
            ));
    }, [openMeetingCircleIds]);

    return (
        <div className="w-full h-full flex justify-center items-center overflow-hidden relative bg-slate-50/50">
            <svg
                ref={svgRef}
                width={dimensions.width}
                height={dimensions.height}
                style={{ display: 'block', maxWidth: '100%', maxHeight: '100%' }}
            />
        </div>
    );
};

const MemoizedCirclePack = React.memo(
    CirclePack,
    (prevProps, nextProps) => (
        prevProps.data === nextProps.data &&
        prevProps.onCircleClick === nextProps.onCircleClick &&
        prevProps.valueAccessor === nextProps.valueAccessor &&
        prevProps.tight === nextProps.tight &&
        String(prevProps.selectedCircleId ?? '') === String(nextProps.selectedCircleId ?? '') &&
        areSetsEqual(prevProps.openMeetingCircleIds, nextProps.openMeetingCircleIds)
    )
);

export default MemoizedCirclePack;
